import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
    VisualizationDtoTypeEnum,
} from '../typescript-axios';
import { FieldType, ParameterSchema } from '../pages/authenticated/dashboards/dashboard/components/visualizations/visualization_parameters_handler';
import { VISUALIZATION_CONSTRAINTS } from '../pages/authenticated/dashboards/dashboard/components/visualizations/config';

// Auxiliary functions to automatically create
const fieldTypeToYup: Record<FieldType, () => Yup.Schema> = {
    string: () => Yup.string(),
    number: () => Yup.number(),
    column: () => Yup.string(),
    boolean: () => Yup.boolean(),
    'string[]': () => Yup.array().of(Yup.string()),
    'column[]': () => Yup.array().of(Yup.string()),
};

function buildYupSchema(schema: ParameterSchema<Record<string, any>>): Yup.ObjectSchema<any> {
    const shape = Object.fromEntries(
        Object.entries(schema).map(([field, fieldSchema]) => {
            let validator = fieldTypeToYup[fieldSchema.type as FieldType]();

            if (fieldSchema.required) {
                validator = (validator as any).required();
            } else {
                validator = (validator as any).nullable().optional();
            }

            if (fieldSchema.allowedValues !== undefined) {
                validator = (validator as any).oneOf(fieldSchema.allowedValues);
            }

            if (fieldSchema.default !== undefined) {
                validator = (validator as any).default(fieldSchema.default);
            }

            return [field, validator];
        })
    );

    return Yup.object(shape);
}

const VISUALIZATION_PARAMETER_SCHEMAS: Record<VisualizationDtoTypeEnum, Yup.ObjectSchema<any>> =
    Object.fromEntries(
        Object.entries(VISUALIZATION_CONSTRAINTS).map(([type, constraints]) => [
            type,
            buildYupSchema(constraints.parameterSchema),
        ])
    ) as Record<VisualizationDtoTypeEnum, Yup.ObjectSchema<any>>;



// Main validation schema
export const useAddVisualizationValidationSchema = (availableDataSources: string[]) => {
    const { t } = useTranslation();

    return Yup.object({
        visualization: Yup.object({
            type: Yup.string()
                .oneOf(Object.values(VisualizationDtoTypeEnum))
                .required(t('validation.required')),
            data_source: Yup.string()
                .oneOf(availableDataSources)
                .required(t('validation.required')),
            position: Yup.object({
                x: Yup.number().min(0).integer(),
                y: Yup.number().min(0).integer()
            }),
            size: Yup.object({
                w: Yup.number().min(0).integer(),
                h: Yup.number().min(0).integer()
            }),
            parameters: Yup.object().when('type', (type, schema) =>
                VISUALIZATION_PARAMETER_SCHEMAS[type[0] as VisualizationDtoTypeEnum] ?? schema
            )
        })
    })
}